import { Alert, Col, Form, Modal, Row } from 'react-bootstrap'
import styles from './CreateUser.module.css'
import DefaultButton from '../../../../../../../components/buttons/default-button/DefaultButton'
import { useCallback, useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'

const CREATE_USER_QUERY = gql`
    mutation CreateUser($data: CreateUserInput!) {
        createUser(data: $data) {
            success
            errorMessage
        }
    }
`

type CreateUserProps = {
    showModal: boolean
    onHide: () => void
    triggerFetch: () => void
}

interface CreateUserResult {
    createUser: {
        success: boolean
        errorMessage: string | null
    }
}

export default function CreateUser({ showModal, onHide, triggerFetch }:CreateUserProps)  {
    const [username, setUsername] = useState("")
    const [enabled, setEnabled] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [showAlert, setShowAlert] = useState(false)
    const [isInvalid, setIsInvalid] = useState<boolean | undefined>(undefined)
    const [success, setSuccess] = useState<boolean | undefined>(undefined)
    const client = useApolloClient()

    const resetCreateForm = useCallback((onHide = false) => {
        if(success || onHide) {
            setUsername("")
        }
        setEnabled(false)
        setErrorMessage("")
    }, [ success ])

    const create = async (enabled: boolean) => {
        if(username.length < 1) {
            setErrorMessage("Felhasználónév kitöltése kötelező")
            setSuccess(false)
            return
        }

        try {
            const data = await client.mutate<CreateUserResult>({
                mutation: CREATE_USER_QUERY,
                variables: {
                    data: {
                        username,
                        enabled
                    }
                },
                fetchPolicy: 'no-cache'
            })
            const result = data.data?.createUser

            if(!result?.success) {
                setErrorMessage(result?.errorMessage ?? "Ismeretlen hiba a szerverről")
                setSuccess(false)
                setIsInvalid(true)
                setShowAlert(true)
                return
            }

            setSuccess(true)
            setIsInvalid(false)
            setShowAlert(true)
            setErrorMessage(result.errorMessage ?? "Sikeres mentés")
            triggerFetch()
        }
        catch(e) {
            console.log(e)
            return {
                errorMessage: 'Hiba a művelet közben, kérjük próbálja meg később',
                success: false
            }
        }
    }

    useEffect(() => {
        let throttle: string | number | NodeJS.Timeout | undefined

        if(showAlert !== false || success !== undefined || isInvalid !== undefined) {
            throttle = setTimeout(() => {
                setShowAlert(false)
                setSuccess(undefined)
                setIsInvalid(undefined)
                resetCreateForm()
            }, 2000)
        }

        return () => clearTimeout(throttle)
    }, [showAlert, success, isInvalid, resetCreateForm])

    return (
        <Modal
            show={ showModal }
            onHide={() => {
                resetCreateForm(true)
                onHide()
            }}
            centered
            dialogClassName={styles.modalStyle}
        >
            <Modal.Header className={styles.modalHeader}>
                <Modal.Title className={styles.modalTitle}>
                    <Row>
                        Új felhasználó létrehozása
                    </Row>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className={styles.modalBody}>
                <Row>
                    <Form id="createUserForm" noValidate>
                        <Form.Group className="mb-3" controlId="username">
                            <Form.Label>Felhasználónév</Form.Label>

                            <Form.Control
                                type="text"
                                value={username}
                                onChange={e => {
                                    setUsername(e.target.value)
                                }}
                                required
                                isValid={success}
                                isInvalid={isInvalid}
                            />

                            <Form.Control.Feedback type="invalid">
                                {errorMessage}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="enabled">
                            <Form.Check
                                type={'switch'}
                                label={`Aktív`}
                                id={`enabled`}
                                checked={enabled}
                                onChange={(e) =>
                                    setEnabled(e.target.checked)
                                }
                            />
                        </Form.Group>
                    </Form>
                </Row>

                <Row className='mb-3 justify-content-center'>
                    <Col md="12">
                        <Alert
                            show={showAlert}
                            variant={success ? 'success' : 'danger'}
                            transition={false}
                            onClose={() => {
                                setShowAlert(false)
                                setSuccess(false)
                            }}
                            dismissible
                        >
                            <b>
                                <span dangerouslySetInnerHTML={{ __html: errorMessage }}></span>
                            </b>
                        </Alert>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer className={`d-flex justify-content-between ${styles.modalFooter}`}>
                <DefaultButton
                    title="Kilépés"
                    onClick={() => {
                        resetCreateForm(true)
                        onHide()
                    }}
                />

                <DefaultButton
                    title="Létrehozás"
                    onClick={() => create(enabled)}
                />
            </Modal.Footer>
        </Modal>
    )
}